// =============================================
// Typy dla modulu Mapy Tematyczne (SCN Generator)
// =============================================

// --- Enumy ---

export type MapStatus = "draft" | "active" | "archived";

// v3.0 — Demand, SCN, Content Engine
export type DemandTier = "clearly_validated" | "validated_moderate" | "weak_signal" | "no_demand" | "unknown";
export type NodeRole = "pillar" | "hub" | "spoke" | "money_node";
export type QaStatus = "pending" | "approved" | "needs_review" | "failed";
export type ContentLifecycleStatus =
  | "not_started" | "context_ready" | "in_generation"
  | "ready" | "needs_review" | "approved"
  | "scheduled" | "published";
export type EngineStage =
  | "pending" | "serp_research" | "kg_assembly" | "fan_out" | "outline"
  | "outline_ready"
  | "draft" | "enrichment" | "optimization" | "humanization"
  | "ready" | "published" | "failed"
  // Existing Posts Awareness — node pominiety bo istnieje matched wpis na WP
  | "skipped_existing";

// --- Existing Posts Awareness ---
export type MatchStatus =
  | "unchecked"
  | "unmatched"
  | "matched_keep"
  | "matched_refurbish"
  | "partial"
  | "skip_manual";

export type MatchMethod = "hash" | "jaccard" | "containment" | "llm_confirmed" | "manual";

// M9 — SRO Query Frame
export type QueryFrame =
  | "definitional"
  | "procedural"
  | "comparative"
  | "causal"
  | "evaluative"
  | "mixed";

// M9 — matching_config
export interface MatchingConfig {
  id: string;
  scope: "global" | "map";
  map_id: string | null;
  containment_auto_threshold: number;
  containment_borderline_threshold: number;
  require_entity_match: boolean;
  require_frame_match_for_auto: boolean;
  send_weak_to_llm: boolean;
  llm_batch_size: number;
  borderline_quality_as: "matched_keep" | "matched_refurbish" | "partial";
  created_at: string;
  updated_at: string;
}

export type QualityRecommendation = "keep" | "borderline" | "refurbish";

export type QualityMethod = "heuristic" | "heuristic_plus_llm";

export type RefurbishImageStrategy = "new" | "keep";

export type WpMatchingRunStatus = "running" | "completed" | "failed";
export type WpMatchingRunMode = "fast" | "deep";
export type QueueStatus = "queued" | "in_progress" | "completed" | "failed" | "cancelled";
export type PublishQueueStatus = "queued" | "scheduled" | "publishing" | "published" | "failed" | "cancelled";

export type EntityType =
  | "concept"
  | "product"
  | "method"
  | "tool"
  | "role"
  | "problem"
  | "attribute";

export type DocumentRole =
  | "definition"
  | "how_to"
  | "comparison"
  | "troubleshooting"
  | "glossary"
  | "history"
  | "commercial"
  | "protocol"
  | "guide";

export type NodeStatus =
  | "planned"
  | "draft"
  | "published"
  | "refresh"
  | "deprecated";

export type RelationType =
  | "parent_of"
  | "sibling"
  | "prerequisite"
  | "next_step"
  | "comparison"
  | "glossary_ref"
  | "entity_ref"
  | "hub_to_spoke";

export type PromptStep =
  | "macro_generation"
  | "seed_generation"
  | "node_generation"
  | "entity_extraction"
  | "eav_extraction"
  | "deduplication"
  | "linking"
  | "content_plan"
  | "content_full"
  // v3.0 — SCN Constitution
  | "sbd_generation"
  | "scn_audit"
  // v3.0 — Content Engine
  | "kg_assembly"
  | "fan_out"
  | "outline_generation"
  | "draft_generation"
  | "enrichment"
  | "optimization"
  | "humanization"
  // v3.1 — 3-callowy KG + 2-etapowy fan-out + osobne optimization passes
  | "entity_extraction_serp"
  | "data_extraction"
  | "fan_out_classification"
  | "fan_out_paa_assignment"
  | "optimization_intermediate"
  | "enrichment_verify"
  | "cluster_dedup"
  | "area_consolidation"
  | "writing_profile_generation"
  | "intro_generation"
  | "section_generation"
  // M10 — Existing Posts Awareness LLM
  | "wp_sro_extraction"
  | "wp_matching"
  // M12 — Quality Audit LLM
  | "wp_quality_audit"
  // M14 — Cluster Fit Classifier (LLM scope)
  | "wp_cluster_fit"
  // SEO Meta generation (przed publikacją)
  | "seo_meta_generation"
  // Content Audit LLM (po publikacji — Layer 6)
  | "content_audit_llm"
  // Standalone Mode (Artykuły) — tryb bez mapy tematycznej (2026-05-24)
  | "standalone_style_from_description"
  | "standalone_style_from_samples"
  | "section_generation_standalone"
  | "link_weaving"
  | "image_prompt_generation"
  // SRO Pillar + Author Page (STOP-A.1 z planu — is_active=TRUE od startu, gated VITE_PILLAR/AUTHOR_PAGE_ENABLED)
  | "outline_generation_pillar"
  | "section_generation_pillar"
  | "intro_generation_pillar"
  | "author_page_generation"
  // Ulepszanie zaplecza — unikalne podstrony + zdjęcie autora (2026-07-21)
  | "unique_subpage_topics"
  | "unique_subpage_generation"
  | "author_photo_prompt"
  | "brand_logo_prompt"
  // Migracja strony — brief designu motywu „brand" (2026-09-23)
  | "static_design_brief"
  | "static_menu_grouping";

// M15 — Decision Engine (deterministyczne, bez LLM — nie w PromptStep)
export type FinalAction =
  | "keep"
  | "refurbish"
  | "refurbish_url_preserve"
  | "keep_outer"
  | "delete"
  | "consolidate_301"
  | "monitor";

export type FinalActionPriority = "high" | "medium" | "low";

// M16 — Map Audit v2 (G3)
export type ClusterType = "core" | "outer";
export type AutoAuditLevel = "full" | "coverage_only" | "off";
export type AuditSeverity = "error" | "warning" | "info" | "ok";
export type AuditVerdict = "pass" | "warn" | "fail";

export interface AuditIssue {
  severity: AuditSeverity;
  dimension: string;
  message: string;
  affected_slugs: string[];
  suggested_fix?: string;
}

export interface AuditConfigThreshold {
  error?: number;
  warning?: number;
}

export interface AuditConfig {
  orphan_ratio?: AuditConfigThreshold;
  duplicate_count?: AuditConfigThreshold;
  duplicate_jaccard?: AuditConfigThreshold;
  seed_imbalance_cv?: AuditConfigThreshold;
  stranded_ratio?: AuditConfigThreshold;
  low_match_ratio?: AuditConfigThreshold;
  max_affected_per_issue?: number;
}

export interface PaaPreviewItem {
  question: string;
  source: "google_paa";
}

export interface AuditReportV2Meta {
  audit_version: "v2";
  audited_at: string;
  config_snapshot: AuditConfig;
  total_score: number;
  max_score: number;
  overall_verdict: AuditVerdict;
  llm_used: boolean;
  llm_cost_usd: number;
  duration_ms: number;
  skipped_by_lock?: boolean;
}

export interface AuditReportV2 {
  meta: AuditReportV2Meta;
  layers: {
    L1?: {
      clampedNodes: number;
      fixedSlugs: number;
      orphansFixed: number;
      boundaryViolations: number;
      needsReviewFlagged: number;
    };
    L2_deterministic?: Record<string, unknown>;
    L2_llm?: Record<string, unknown>;
    L3?: Record<string, unknown>;
    L5_coverage?: Record<string, unknown>;
  };
  legacy_scn_audit?: Record<string, unknown>;
}

export type PromptModel =
  // Claude 5 (2026-08) — claude-opus-5 $5/$25, claude-sonnet-5 $3/$15. NIGDY claude-fable-5.
  | "claude-opus-5"
  | "claude-sonnet-5"
  | "claude-opus-4-8"
  | "claude-opus-4-6"
  | "claude-sonnet-4-6"
  | "claude-sonnet-4-5"
  | "claude-sonnet-4-0"
  | "claude-haiku-4-5"
  // OpenAI — uzywane w DB (potwierdzone SELECT DISTINCT model FROM thematic_prompts)
  | "gpt-4.1"
  | "gpt-4.1-mini"
  | "gpt-5.4"
  | "gpt-5.4-mini";

export const PROMPT_MODELS: { value: PromptModel; label: string }[] = [
  { value: "claude-opus-5", label: "Opus 5 (najmadrzejszy)" },
  { value: "claude-sonnet-5", label: "Sonnet 5 (najlepszy balans)" },
  { value: "claude-haiku-4-5", label: "Haiku 4.5 (najtanszy, najszybszy)" },
  { value: "claude-opus-4-8", label: "Opus 4.8 (legacy)" },
  { value: "claude-opus-4-6", label: "Opus 4.6 (legacy)" },
  { value: "claude-sonnet-4-6", label: "Sonnet 4.6 (legacy)" },
  { value: "claude-sonnet-4-5", label: "Sonnet 4.5 (legacy)" },
  { value: "claude-sonnet-4-0", label: "Sonnet 4.0 (legacy)" },
  { value: "gpt-4.1", label: "GPT-4.1 (OpenAI, outline/structure)" },
  { value: "gpt-4.1-mini", label: "GPT-4.1 Mini (OpenAI, dedup/audit)" },
  { value: "gpt-5.4", label: "GPT-5.4 (OpenAI, reasoning)" },
  { value: "gpt-5.4-mini", label: "GPT-5.4 Mini (OpenAI, default content)" },
];

export type GenerationRunStatus = "running" | "completed" | "failed";

export type ContentStatus =
  | "empty"
  | "plan_ready"
  | "content_ready"
  | "approved";

export type EAVValueType = "text" | "number" | "boolean" | "enum";
export type EAVSource = "ai" | "manual" | "inferred";

// --- Modele DB ---

export interface ThematicMap {
  id: string;
  user_id: string;
  website_id: string | null;
  name: string;
  macro_statement: string;
  scope_in: string[];
  scope_out: string[];
  audience: string;
  language: string;
  map_version: string;
  status: MapStatus;
  active_profile_id: string | null;
  // v3.0 — Semantic Boundary Declaration
  boundaries_owned: string[];
  boundaries_not_owned: string[];
  boundaries_adjacent: string[];
  boundaries_updated_at: string | null;
  // v3.0 — SCN Audit
  audit_report: SCNAuditResult | Record<string, never>;
  audit_score: number | null;
  audited_at: string | null;
  // Existing Posts Awareness
  wp_posts_synced_at?: string | null;
  wp_posts_count?: number;
  existing_posts_enabled?: boolean;
  use_llm_quality_check?: boolean;
  // Workspace — grupuje mapy per kontekst (firma vs sideprojects)
  workspace?: "firma" | "sideprojects";
  // STOP-A migracje SRO — feature flag Pillar Page.
  // Cluster Coverage + Entity Salience USUNIETE 2026-07-21 (kolumny zdropowane w DB).
  pillar_pages_enabled?: boolean;
  created_at: string;
  updated_at: string;
}

export interface ThematicEntity {
  id: string;
  user_id: string;
  map_id: string;
  canonical_name: string;
  entity_type: EntityType;
  aliases: string[];
  description: string;
  created_at: string;
  updated_at: string;
}

export type SeedStatus =
  | "active"
  | "pending_production"
  | "draft"
  | "deprecated"
  | "archived";

export interface ThematicSeed {
  id: string;
  user_id: string;
  map_id: string;
  name: string;
  slug: string;
  intent_family: string[];
  priority: number;
  hub_content: string;
  hub_title: string;
  hub_slug_override: string;
  hub_meta: Record<string, unknown>;
  sort_order: number;
  // v3 — SRO framework metadata (from seed_generation v3 prompt)
  seed_type?: string | null;
  scope_coverage?: string[] | null;
  // M18 — Map Repair Engine v1: status lifecycle
  status?: SeedStatus | null;
  // is_hub removed in CLEANUP v1 #3 (2026-06-01): po faza P model "1 pillar per seed"
  // (idx_unique_pillar_per_seed na thematic_nodes) kolumna unused.
  created_at: string;
  updated_at: string;
}

export interface ThematicNode {
  id: string;
  user_id: string;
  map_id: string;
  seed_id: string;
  title: string;
  slug: string;
  document_role: DocumentRole;
  primary_intent: string;
  primary_entity_raw: string;
  required_entities_raw: string[];
  primary_entity_id: string | null;
  required_entity_ids: string[];
  canonical_question: string;
  canonical_question_hash: string;
  node_status: NodeStatus;
  map_version: string;
  sort_order: number;
  // v3.0 — Demand Validation
  search_volume: number | null;
  cpc: number | null;
  keyword_difficulty: number | null;
  commercial_score: number | null;
  demand_tier: DemandTier;
  demand_validated_at: string | null;
  demand_source: Record<string, unknown>;
  // v3.0 — Node Role
  node_role: NodeRole;
  publish_priority: number;
  // Existing Posts Awareness — matching z wpisami WP
  matched_wp_post_id?: string | null;
  match_status?: MatchStatus;
  match_confidence?: number | null;
  match_reason?: string | null;
  match_method?: MatchMethod | null;
  matched_at?: string | null;
  refurbish_approved_by_user?: boolean;
  refurbish_approved_at?: string | null;
  refurbish_approved_by?: string | null;
  refurbished_at?: string | null;
  refurbish_reason?: string | null;
  refurbish_image_strategy?: RefurbishImageStrategy;
  existing_wp_quality_score?: number | null;
  existing_wp_word_count?: number | null;
  // M9 — SRO
  canonical_question_tokens_normalized?: string[] | null;
  query_frame?: QueryFrame | null;
  created_at: string;
  updated_at: string;
}

export interface ThematicNodeRelation {
  id: string;
  user_id: string;
  map_id: string;
  from_node_id: string;
  to_node_id: string;
  relation_type: RelationType;
  weight: number;
  created_at: string;
}

export interface ThematicPrompt {
  id: string;
  step: PromptStep;
  prompt_template: string;
  model: PromptModel;
  provider?: "claude" | "openai" | null;
  model_id?: string | null;
  /** Snapshot efektywnego modelu runtime z 2026-08-23 (migracja prompts_no_versioning).
   *  Panel pokazuje ostrzeżenie + „Przywróć rekomendowany", gdy model_id odjedzie. */
  recommended_model?: string | null;
  temperature?: number | null;
  is_active: boolean;
  /** Od migracji prompts_no_versioning zawsze 1 (1 wiersz = 1 step); kolumna została dla kompatybilności typów. */
  version: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ThematicGenerationRun {
  id: string;
  user_id: string;
  map_id: string;
  step: string;
  input_json: Record<string, unknown>;
  prompt_used: string;
  model: string;
  output_json: Record<string, unknown> | null;
  tokens_input: number;
  tokens_output: number;
  cost_usd: number;
  status: GenerationRunStatus;
  error_message: string | null;
  duration_ms: number;
  created_at: string;
}

export interface ThematicNodeContent {
  id: string;
  user_id: string;
  map_id: string;
  node_id: string;
  seo_title: string | null;
  meta_description: string | null;
  opening_answer: string | null;
  h2_outline: string[];
  key_points: string[];
  faq: string[];
  do_not_include: string[];
  internal_links: Array<{
    slug: string;
    title: string;
    anchor: string;
    reason?: string;
  }>;
  word_count_target: number;
  content_markdown: string | null;
  content_html: string | null;
  content_status: ContentStatus;
  plan_generated_at: string | null;
  content_generated_at: string | null;
  plan_model: string | null;
  content_model: string | null;
  plan_tokens: number;
  content_tokens: number;
  // v3.0 — Content Engine pipeline
  fan_out: Record<string, unknown>;
  outline: Record<string, unknown>;
  distribution: Record<string, unknown>;
  draft_html: string;
  enriched_html: string;
  optimized_html: string;
  final_html: string;
  engine_stage: EngineStage;
  engine_started_at: string | null;
  engine_completed_at: string | null;
  engine_error: string | null;
  engine_cost: Record<string, unknown>;
  // v3.0 — Publishing
  published_at: string | null;
  wp_post_id: number | null;
  wp_post_url: string | null;
  // v3.0 — Featured Image (FAL.AI)
  featured_image_url: string | null;
  image_prompt: string | null;
  image_model: string | null;
  // v3.0 — Content Lifecycle
  generated_from_context_hash: string | null;
  current_context_hash: string | null;
  is_stale: boolean;
  needs_regeneration: boolean;
  last_context_checked_at: string | null;
  qa_status: QaStatus;
  quality_score: number | null;
  qa_checked_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ThematicEntityAttribute {
  id: string;
  user_id: string;
  map_id: string;
  entity_id: string;
  attribute_name: string;
  value: string;
  value_type: EAVValueType;
  source: EAVSource;
  confidence: number;
  notes: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export type DifficultyLevel =
  | "beginner"
  | "beginner_intermediate"
  | "intermediate"
  | "advanced";
export type VoicePronouns = "ty" | "wy" | "pan_pani" | "bezosobowo";
export type Verbosity = "zwiezle" | "standard" | "obszernie";
export type SentenceLength = "short" | "mixed" | "long";
export type AnalogyFrequency = "none" | "rare" | "moderate" | "frequent";
export type ClaimStrength = "cautious" | "moderate" | "strong";
export type GlossaryMode = "none" | "first_mention" | "always_explain";
export type TableUsage = "never" | "when_useful" | "frequently";

export interface ThematicWritingProfile {
  id: string;
  user_id: string;
  map_id: string;
  // Tab 1: Tożsamość
  name: string;
  author_identity: string;
  mission: string;
  audience_primary: string;
  audience_secondary: string;
  difficulty_level: DifficultyLevel;
  // Tab 2: Style DNA
  voice_pronouns: VoicePronouns;
  verbosity: Verbosity;
  sentence_length: SentenceLength;
  analogy_frequency: AnalogyFrequency;
  analogy_domain: string;
  preferred_phrases: string[];
  banned_phrases: string[];
  language_rules: string;
  // Tab 3: Struktura
  required_sections: string[];
  h2_patterns: Record<string, string>;
  faq_rules: string;
  use_tables: TableUsage;
  media_preferences: string;
  // Tab 4: Dowody
  evidence_style: string;
  claim_strength: ClaimStrength;
  disclaimer_template: string;
  example_policy: string;
  measurement_policy: string;
  // Tab 5: Słownik
  glossary_mode: GlossaryMode;
  abbreviation_rule: string;
  core_entities: string[];
  units_standards: string;
  // Tab 6: Prompty
  content_plan_prompt: string;
  content_full_prompt: string;
  // Meta
  language: string;
  is_active: boolean;
  version: number;
  created_at: string;
  updated_at: string;
  // v6 — auto-generated profile
  profile_name?: string;
  public_author_label?: string;
  schema_version?: number;
  generated_from?: string; // 'manual' | 'auto_llm'
  model_used?: string;
  generated_at?: string;
  // v7 — voice DNA (replaces preferred_phrases in pipeline)
  voice_characteristics?: string[];
  content_personality?: string;
  // Schema.org author + STOP-A author_page (C1)
  author_bio_html?: string | null;
  author_url?: string | null;
  author_slug?: string | null;
  author_photo_url?: string | null;
  author_job_title?: string | null;
  author_same_as?: string[];
  author_knows_about?: string[];
  schema_enabled?: boolean;
  author_page_enabled?: boolean;
  author_page_wp_post_id?: number | null;
  author_page_generated_at?: string | null;
  author_page_html?: string | null;
}

// --- Typy input (do tworzenia) ---

export type NewThematicMap = Omit<
  ThematicMap,
  "id" | "created_at" | "updated_at"
>;
export type UpdateThematicMap = Partial<
  Omit<ThematicMap, "id" | "user_id" | "created_at" | "updated_at">
>;

export type NewThematicSeed = Omit<
  ThematicSeed,
  "id" | "created_at" | "updated_at"
>;
export type UpdateThematicSeed = Partial<
  Omit<ThematicSeed, "id" | "user_id" | "map_id" | "created_at" | "updated_at">
>;

export type NewThematicNode = Omit<
  ThematicNode,
  "id" | "created_at" | "updated_at"
>;
export type UpdateThematicNode = Partial<
  Omit<ThematicNode, "id" | "user_id" | "map_id" | "created_at" | "updated_at">
>;

export type NewThematicEntity = Omit<
  ThematicEntity,
  "id" | "created_at" | "updated_at"
>;
export type UpdateThematicEntity = Partial<
  Omit<
    ThematicEntity,
    "id" | "user_id" | "map_id" | "created_at" | "updated_at"
  >
>;

export type NewThematicNodeRelation = Omit<
  ThematicNodeRelation,
  "id" | "created_at"
>;

export type NewThematicNodeContent = Omit<
  ThematicNodeContent,
  "id" | "created_at" | "updated_at"
>;
export type UpdateThematicNodeContent = Partial<
  Omit<
    ThematicNodeContent,
    "id" | "user_id" | "map_id" | "node_id" | "created_at"
  >
>;

export type NewThematicEntityAttribute = Omit<
  ThematicEntityAttribute,
  "id" | "created_at" | "updated_at"
>;
export type UpdateThematicEntityAttribute = Partial<
  Omit<ThematicEntityAttribute, "id" | "user_id" | "map_id" | "created_at">
>;

export type NewThematicWritingProfile = Omit<
  ThematicWritingProfile,
  "id" | "created_at" | "updated_at"
>;
export type UpdateThematicWritingProfile = Partial<
  Omit<ThematicWritingProfile, "id" | "user_id" | "map_id" | "created_at">
>;

// --- Typy rozszerzone (z joinami) ---

/** Rozbicie pipeline'u treści per mapa (z thematic_node_content.engine_stage). */
export interface MapPipelineStats {
  total: number;
  pending: number; // engine_stage = 'pending' (nie ruszone)
  working: number; // serp_research|kg_assembly|fan_out|outline|draft|enrichment|optimization|humanization
  brief: number; // 'outline_ready' — struktura/brief gotowy
  ready: number; // 'ready' — treść gotowa do publikacji
  published: number; // 'published'
  indexed: number; // published z indexed_at != null (dodane do indexera)
  skipped: number; // 'skipped_existing' — matched do istniejącego WP
  failed: number; // 'failed'
}

/** Rozbicie ról nodów per mapa (z thematic_nodes.node_role). */
export interface MapRoleStats {
  pillar: number;
  hub: number;
  spoke: number;
  money: number; // money_node
}

export interface ThematicMapWithStats extends ThematicMap {
  seeds_count: number;
  nodes_count: number;
  website_name?: string;
  website_url?: string;
  schema_enabled?: boolean; // websites.schema_enabled (czy schema.org włączona dla domeny)
  // --- rozszerzone staty dla listy map (recon 2026-06-19) ---
  entities_count?: number;
  relations_count?: number;
  pipeline?: MapPipelineStats;
  roles?: MapRoleStats;
  schema_count?: number; // ile nodów ma wstrzykniętą schemę (schema_injection_logs)
  has_profile?: boolean; // active_profile_id != null
  profile_name?: string | null; // nazwa Writing Profile (thematic_writing_profiles.name)
  audit_llm_used?: boolean; // audit_report.meta.llm_used
  repair_status?: string | null;
  last_repaired_at?: string | null;
  last_activity?: string | null; // max(updated_at, audited_at, last_repaired_at, wp_posts_synced_at, last_generation_at)
}

export interface ThematicSeedWithNodes extends ThematicSeed {
  nodes: ThematicNode[];
  nodes_count: number;
}

export interface ThematicNodeWithEntity extends ThematicNode {
  primary_entity?: ThematicEntity;
  seed_name?: string;
}

// --- Typy dla walidacji outputu LLM ---

export interface LLMSeedOutput {
  name: string;
  slug: string;
  intent_family: string[];
  priority: number;
  // v3 — SRO framework metadata (optional, from seed_generation v3 prompt)
  seed_type?: string;
  scope_coverage?: string[];
}

export interface LLMNodeOutput {
  title: string;
  slug: string;
  document_role: DocumentRole;
  primary_intent: string;
  primary_entity: string;
  canonical_question: string;
  required_entities: string[];
}

export interface LLMEntityOutput {
  canonical_name: string;
  entity_type: EntityType;
  aliases: string[];
  description: string;
}

export interface LLMRelationOutput {
  from_slug: string;
  to_slug: string;
  relation_type: RelationType;
  weight: number;
}

export interface LLMMacroOutput {
  macro_statement: string;
  scope_in: string[];
  scope_out: string[];
}

export interface LLMDeduplicationOutput {
  same_intent: "yes" | "no" | "maybe";
  confidence: number;
  reason: string;
}

export interface LLMContentPlanOutput {
  seo_title: string;
  meta_description: string;
  opening_answer: string;
  h2_outline: string[];
  key_points: string[];
  faq: string[];
  do_not_include: string[];
  internal_links: Array<{
    slug: string;
    title: string;
    anchor: string;
    reason: string;
  }>;
}

export interface LLMContentFullOutput {
  content_markdown: string;
}

export interface LLMEAVAttributeOutput {
  attribute_name: string;
  value: string;
  value_type: EAVValueType;
  confidence: number;
}

export interface LLMEAVOutput {
  entity_canonical_name: string;
  attributes: LLMEAVAttributeOutput[];
}

// --- Typy preview (3 kolory) ---

export type DuplicateLevel = "unique" | "warning" | "duplicate";

export interface NodePreview extends LLMNodeOutput {
  duplicate_level: DuplicateLevel;
  duplicate_reason?: string;
  duplicate_match_slug?: string;
  canonical_question_hash: string;
}

// --- Typy batch node generation ---

export interface BatchNodeResult {
  seedId: string;
  seedName: string;
  nodes: NodePreview[];
  error?: string;
}

// --- Enumy jako const arrays (do walidacji) ---

export const DOCUMENT_ROLES: DocumentRole[] = [
  "definition",
  "how_to",
  "comparison",
  "troubleshooting",
  "glossary",
  "history",
  "commercial",
  "protocol",
  "guide",
];

export const ENTITY_TYPES: EntityType[] = [
  "concept",
  "product",
  "method",
  "tool",
  "role",
  "problem",
  "attribute",
];

export const RELATION_TYPES: RelationType[] = [
  "parent_of",
  "sibling",
  "prerequisite",
  "next_step",
  "comparison",
  "glossary_ref",
  "entity_ref",
  "hub_to_spoke",
];

export const NODE_STATUSES: NodeStatus[] = [
  "planned",
  "draft",
  "published",
  "refresh",
  "deprecated",
];

export const PROMPT_STEPS: PromptStep[] = [
  "macro_generation",
  "seed_generation",
  "node_generation",
  "entity_extraction",
  "eav_extraction",
  "deduplication",
  "linking",
  "content_plan",
  "content_full",
  // v3.0 — SCN Constitution
  "sbd_generation",
  "scn_audit",
  // v3.0 — Content Engine
  "kg_assembly",
  "fan_out",
  "fan_out_classification",
  "fan_out_paa_assignment",
  "cluster_dedup",
  "area_consolidation",
  "writing_profile_generation",
  "outline_generation",
  "intro_generation",
  "section_generation",
  "draft_generation",
  "enrichment",
  "optimization",
  "optimization_intermediate",
  "humanization",
  // M10 — Existing Posts Awareness (SRO)
  "wp_sro_extraction",
  "wp_matching",
  // M12 — Quality Audit LLM
  "wp_quality_audit",
  // M14 — Cluster Fit Classifier (LLM scope)
  "wp_cluster_fit",
  // SEO Meta generation
  "seo_meta_generation",
  // Content Audit LLM (Layer 6 — po publikacji)
  "content_audit_llm",
];

export const EAV_VALUE_TYPES: EAVValueType[] = [
  "text",
  "number",
  "boolean",
  "enum",
];
export const EAV_SOURCES: EAVSource[] = ["ai", "manual", "inferred"];

// v3.0 const arrays
export const DEMAND_TIERS: DemandTier[] = [
  "clearly_validated",
  "validated_moderate",
  "weak_signal",
  "no_demand",
  "unknown",
];

export const NODE_ROLES: NodeRole[] = ["pillar", "hub", "spoke", "money_node"];

export const ENGINE_STAGES: EngineStage[] = [
  "pending",
  "serp_research",
  "kg_assembly",
  "fan_out",
  "outline",
  "draft",
  "enrichment",
  "optimization",
  "humanization",
  "ready",
  "published",
  "failed",
];

// =============================================
// v3.0 — Nowe interfejsy
// =============================================

export interface SemanticBoundary {
  owned: string[];
  not_owned: string[];
  adjacent: string[];
}

export interface DemandValidationResult {
  nodeId: string;
  tier: DemandTier;
  searchVolume: number;
  cpc: number;
  kd: number;
  commercialScore: number;
  nodeRole: NodeRole;
}

export interface DataForSEOKeywordData {
  keyword: string;
  search_volume: number;
  cpc: number;
  competition: number;
  keyword_difficulty: number;
  monthly_searches?: Array<{ month: string; search_volume: number }>;
}

export interface GroundingContext {
  mapMacro: string;
  boundaries: SemanticBoundary;
  demandSignals: Array<{
    nodeTitle: string;
    tier: DemandTier;
    searchVolume: number;
    commercialScore: number;
  }>;
  entityContext: Array<{
    name: string;
    type: string;
    topAttributes: Array<{ attribute: string; value: string }>;
  }>;
  nodeRelations: Array<{
    from: string;
    to: string;
    type: string;
  }>;
}

export interface SCNAuditDimensionResult {
  dimension: string;
  score: number;
  maxScore: number;
  issues: Array<{
    severity: "critical" | "warning" | "info";
    message: string;
    fix?: string;
  }>;
}

export interface SCNAuditResult {
  overall: number;
  maxOverall: number;
  dimensions: SCNAuditDimensionResult[];
  criticalIssues: number;
  autoFixApplied: number;
  auditedAt: string;
}

// v3.0 — DB model interfaces

export interface DataForSeoCache {
  id: string;
  query: string;
  query_type: string;
  location_code: number;
  language_code: string;
  data: Record<string, unknown>;
  fetched_at: string;
  expires_at: string;
  created_at: string;
}

export interface ContentSerpCache {
  id: string;
  map_id: string;
  node_id: string;
  query: string;
  engine: string;
  results: unknown[];
  extracted_content: unknown[];
  fetched_at: string;
  expires_at: string;
  created_at: string;
}

export interface ContentKnowledgeGraph {
  id: string;
  map_id: string;
  node_id: string;
  kg_data: Record<string, unknown>;
  entities: unknown[];
  facts: unknown[];
  relations: unknown[];
  sources: unknown[];
  source_types: string[];
  created_at: string;
  updated_at: string;
}

export interface GenerationQueueItem {
  id: string;
  user_id: string;
  map_id: string;
  node_id: string;
  step: string;
  status: QueueStatus;
  priority: number;
  config: Record<string, unknown>;
  result: Record<string, unknown> | null;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PublishQueueItem {
  id: string;
  user_id: string;
  map_id: string;
  node_id: string;
  website_id: string | null;
  wp_post_id: number | null;
  wp_post_url: string | null;
  status: PublishQueueStatus;
  scheduled_at: string | null;
  published_at: string | null;
  error: string | null;
  retry_count: number;
  created_at: string;
  updated_at: string;
}

// =============================================
// NodeContentContext — warstwa pośrednia mapa → Content Engine
// =============================================

export interface PostContentAuditResult {
  quality_score: number;
  qa_status: QaStatus;
  has_sources: boolean;
  has_internal_links: boolean;
  passes_boundary_check: boolean;
  required_entities_coverage: number;
  issues: Array<{
    code: string;
    severity: "critical" | "warning" | "info";
    message: string;
  }>;
}

export interface NodeContentContext {
  map: {
    id: string;
    name: string;
    macro_statement: string;
    scope_in: string[];
    scope_out: string[];
    boundaries_owned: string[];
    boundaries_not_owned: string[];
    boundaries_adjacent: string[];
    language: string;
  };
  seed: {
    id: string;
    name: string;
    slug: string;
    intent_family: string[];
    priority: number;
  };
  node: {
    id: string;
    title: string;
    slug: string;
    document_role: string;
    primary_intent: string;
    canonical_question: string;
    primary_entity: string;
    required_entities: string[];
  };
  demand: {
    search_volume: number | null;
    cpc: number | null;
    keyword_difficulty: number | null;
    commercial_score: number | null;
    demand_tier: string;
    node_role: string;
    publish_priority: number;
  };
  knowledge: {
    entities: Array<{
      canonical_name: string;
      entity_type: string;
      aliases: string[];
      description: string;
    }>;
    eav: Array<{
      entity_name: string;
      attribute_name: string;
      value: string;
      confidence: number;
    }>;
    relations: Array<{
      from_title: string;
      from_slug: string;
      to_title: string;
      to_slug: string;
      relation_type: string;
      weight: number;
    }>;
    internal_link_suggestions: Array<{
      target_slug: string;
      target_title: string;
      relation_type: string;
    }>;
  };
  writingProfile: {
    author_identity: string;
    voice_pronouns: string;
    verbosity: string;
    sentence_length: string;
    preferred_phrases: string[];
    banned_phrases: string[];
    claim_strength: string;
    evidence_style: string;
    analogy_frequency: string;
    analogy_domain: string;
    glossary_mode: string;
    language_rules: string;
  } | null;
  /** Reverse SERP validation data (from demand_source JSONB) */
  serp: {
    validated_question: string | null;
    best_market_keyword: string | null;
    market_keywords_top5: Array<{ keyword: string; search_volume: number }>;
    serp_decision: string | null;
    confidence: string | null;
    serp_mode: string | null;
    top3_competitors: Array<{ domain: string; title: string }>;
    candidate_sv: number;
    market_keyword_sv: number;
  } | null;
  /**
   * Existing Posts Awareness — meta o istniejacym wpisie gdy node jest matched_refurbish.
   * CELOWO bez starego HTML (LLM "ulepsza" zamiast pisac od zera).
   * Wypelniane TYLKO dla matched_refurbish. Uzywane w kodzie/logach — NIE wstrzykiwane do promptow.
   */
  refurbish_context?: {
    is_refurbish: boolean;
    existing_wp_post_url: string;
    existing_wp_title: string;
    existing_wp_word_count: number | null;
    existing_wp_red_flags: string[];
  } | null;
}

// v3.0 — LLM output types for SCN automation

export interface LLMSBDOutput {
  owned: string[];
  not_owned: string[];
  adjacent: string[];
  rationale: string;
}

export interface LLMAuditCheckOutput {
  dimension: string;
  score: number;
  issues: Array<{
    severity: "critical" | "warning" | "info";
    message: string;
    fix?: string;
  }>;
}

// =============================================
// Existing Posts Awareness — WordPress layer
// =============================================

export interface WpPost {
  id: string;
  user_id: string;
  website_id: string;
  wp_post_id: number;
  title: string;
  slug: string | null;
  status: "publish" | "draft" | "pending" | "private" | "trash" | "future";
  excerpt: string | null;
  content: string | null;
  content_hash: string | null;
  keyword: string | null;
  published_at: string | null;
  modified_at: string | null;
  synced_at: string | null;
  categories: unknown;
  tags: unknown;
  featured_image_url: string | null;
  wp_url: string | null;
  // M2 extensions
  content_plaintext?: string | null;
  meta_description?: string | null;
  word_count?: number | null;
  h2_count?: number | null;
  h3_count?: number | null;
  outbound_links_count?: number | null;
  internal_links_count?: number | null;
  image_count?: number | null;
  quality_score?: number | null;
  quality_red_flags?: string[];
  quality_recommendation?: QualityRecommendation | null;
  quality_assessed_at?: string | null;
  quality_method?: QualityMethod;
  content_fetched_at?: string | null;
  // M8 — SRO extraction (LLM wypelnia)
  first_100_words?: string | null;
  extracted_entity?: string | null;
  extracted_attribute?: string | null;
  extracted_query_frame?: QueryFrame | null;
  sro_extracted_at?: string | null;
  // M9 — pre-computed tokens + extra SRO fields
  title_tokens_normalized?: string[] | null;
  first_100_tokens_normalized?: string[] | null;
  first_paragraph_answer?: string | null;
  supporting_entities?: string[];
  created_at: string;
  updated_at: string;
}

export interface WpPostRefurbishBackup {
  id: string;
  wp_post_id: string;
  node_id: string;
  map_id: string | null;
  website_id: string | null;
  // Snapshot starego wpisu (przed UPDATE)
  old_title: string;
  old_content: string;
  old_excerpt: string | null;
  old_status: string | null;
  old_featured_media_id: number | null;
  old_featured_media_url: string | null;
  old_published_at: string | null;
  old_modified_at: string | null;
  // Meta nowej tresci
  new_title: string | null;
  new_word_count: number | null;
  new_quality_score: number | null;
  // Audit
  backup_at: string;
  created_by: string | null;
  restored_at: string | null;
  restored_by: string | null;
  wp_rest_response: Record<string, unknown> | null;
}

export interface WpMatchingRun {
  id: string;
  map_id: string;
  started_at: string;
  completed_at: string | null;
  status: WpMatchingRunStatus;
  mode: WpMatchingRunMode | null;
  nodes_total: number | null;
  wp_posts_total: number | null;
  matched_keep_count: number;
  matched_refurbish_count: number;
  partial_count: number;
  unmatched_count: number;
  llm_calls: number;
  llm_tokens_used: number;
  llm_cost_usd: number;
  error_message: string | null;
  triggered_by: string | null;
}

export interface WpHtmlMetrics {
  word_count: number;
  char_count: number;
  h1_count: number;
  h2_count: number;
  h3_count: number;
  h4_count: number;
  paragraph_count: number;
  list_count: number;
  image_count: number;
  outbound_links: string[];
  internal_links: string[];
  plaintext: string;
  first_paragraph: string;
  keyword_stuffing_score: number;
  top_words: Array<{ word: string; frequency: number; density_pct: number }>;
}

export interface QualityAssessment {
  score: number;
  red_flags: string[];
  recommendation: QualityRecommendation;
  metrics: WpHtmlMetrics;
  assessed_at: string;
  method: QualityMethod;
}

export interface WpLinkCandidate {
  wp_post_id: string;
  wp_post_internal_id: number;
  title: string;
  slug: string | null;
  url: string | null;
  quality_score?: number;
  entity_overlap_score?: number;
}

export interface MatchingSummary {
  total_nodes: number;
  unchecked: number;
  unmatched: number;
  matched_keep: number;
  matched_refurbish: number;
  matched_refurbish_approved: number;
  partial: number;
  skip_manual: number;
  wp_posts_in_scope: number;
  wp_posts_with_quality: number;
  quality_breakdown?: { keep: number; borderline: number; refurbish: number };
}
